import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { submitFormOnEnter } from '@databases/form/Form';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/database-view';
import { DatabaseFieldType, NumberFormats } from '@databases/types/database';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
  TEXT_VALUE_MIN_TEXT_AREA_HEIGHT,
  useAdjustTextAreaDimensions
} from './popover-edit-common/index.ts';
import { type EditComponentProps } from './types.ts';

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row-reverse',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog' // Ensure container sits above blue dots
});

const firstFieldStylings = xcss({
  flexDirection: 'row'
});

const textAreaMinHeightStyling = xcss({
  minHeight: 'size.400'
});

const NumberValuePopoverEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.NUMBER>
> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isInvalid,
  field,
  isDefaultValue,
  isRenderedInSidePanel,
  isRenderedInContextualFieldMenu,
  isBulkEdit,
  contentId,
  ariaLabel,
  initialKey,
  placeholder
}) => {
  const { locale } = useIntl();
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [inputValue, setInputValue] = useState(
    initialKey ?? (value !== '' ? getNumberDisplayString(value, field, locale) : '')
  );
  const [isFocused, setIsFocused] = useState(false);
  const isCompactDefaultValueInput =
    Boolean(isDefaultValue) && Boolean(isRenderedInContextualFieldMenu);

  useEffect(() => {
    if (initialKey !== undefined) {
      onChange(initialKey, { type: DatabaseFieldType.NUMBER, value: initialKey });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const store = defaultStoreUtils.useStore();

  const adjustTextAreaDimensions = useAdjustTextAreaDimensions({
    textAreaRef,
    maxWidthReachedRef,
    maxPopoverEditWidth: isRenderedInSidePanel
      ? textAreaRef.current?.scrollWidth
      : MAX_POPOVER_EDIT_WIDTH,
    minTextAreaHeight: isCompactDefaultValueInput
      ? TEXT_VALUE_MIN_TEXT_AREA_HEIGHT
      : undefined,
    shouldExpandFromCell: !isCompactDefaultValueInput
  });

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  const fieldIds = selectFieldIdsByContentId(store.getState(), {
    contentId
  });

  const isFirstField = fieldIds.indexOf(field.id) === 0;

  function sanitizeNumberInputValue(inputValue: string): string {
    const isNegative = inputValue.startsWith('-');

    const sanitizedInput = inputValue.replace(/[^0-9.,]/g, '');

    return isNegative ? '-' + sanitizedInput : sanitizedInput;
  }

  const parseInput = useCallback(
    (localizedString: string) => {
      const formatter = new Intl.NumberFormat(locale);

      const parts = formatter.formatToParts(12345.6);
      let decimalSeparator = '.';
      let groupingSeparator = ',';
      for (const part of parts) {
        if (part.type === 'decimal') {
          decimalSeparator = part.value;
        } else if (part.type === 'group') {
          groupingSeparator = part.value;
        }
      }

      let withoutGroupingSeparators = localizedString.split(groupingSeparator).join('');

      // Take only the first occurrence of the decimal separator
      const decimalIndex = withoutGroupingSeparators.indexOf(decimalSeparator);
      if (decimalIndex !== -1) {
        withoutGroupingSeparators =
          withoutGroupingSeparators.slice(0, decimalIndex) +
          '.' +
          withoutGroupingSeparators
            .slice(decimalIndex + 1)
            .split(decimalSeparator)
            .join('');
      }

      return sanitizeNumberInputValue(withoutGroupingSeparators);
    },
    [locale]
  );

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLTextAreaElement>): void => {
      let parsedValue = parseInput(e.currentTarget.value);
      setInputValue(e.currentTarget.value);

      if (
        parsedValue !== '' &&
        field.settings.numberFormat.format === NumberFormats.PERCENT
      ) {
        parsedValue = (Number(parsedValue) / 100).toString();
      }

      onChange(parsedValue, {
        type: DatabaseFieldType.NUMBER,
        value: parsedValue
      });
    },
    [parseInput, field.settings.numberFormat.format, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => submitFormOnEnter(e),
    []
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      let cleanedValue = e.currentTarget.value;

      // Remove suffix if number format is percent
      if (field.settings.numberFormat.format === NumberFormats.PERCENT) {
        cleanedValue = cleanedValue.replace(/\s*%\s*/g, '');
      }

      // Remove prefix if it exists
      const prefix = field.settings.numberFormat.prefix;
      if (prefix && prefix !== '') {
        if (cleanedValue.startsWith(prefix)) {
          cleanedValue = cleanedValue.slice(prefix.length);
        }
      }

      // Update the input value if it changed
      if (cleanedValue !== e.currentTarget.value) {
        setInputValue(cleanedValue);
      }

      // Put cursor to the end of the field
      const cursorPosition = cleanedValue.length;
      e.currentTarget.setSelectionRange(cursorPosition, cursorPosition);

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [
      adjustTextAreaDimensions,
      onFocus,
      field.settings.numberFormat.format,
      field.settings.numberFormat.prefix
    ]
  );

  const handlePaste = useCallback(() => {
    // Using setTimeout to ensure the pasted content is available in the textarea
    // Setting to 10 milliseconds to maximize the amount of time while NOT being
    // perceivable to humans
    setTimeout(() => {
      adjustTextAreaDimensions();
    }, 10);
  }, [adjustTextAreaDimensions]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
    setInputValue(value !== '' ? getNumberDisplayString(value, field, locale) : '');
  }, [field, locale, onBlur, value]);

  return (
    <Box
      testId={`number-value-edit-expand-${isFirstField ? 'right' : 'left'}`}
      xcss={[
        textAreaContainerStyling,
        isFirstField && firstFieldStylings,
        isRenderedInSidePanel &&
          !isRenderedInContextualFieldMenu &&
          textAreaMinHeightStyling
      ]}
    >
      <TextArea
        id={'NumberTextArea'}
        style={{
          border: isCompactDefaultValueInput
            ? `${token('border.width', '1px')} solid ${
                isFocused ? token('color.border.focused') : token('color.border')
              }`
            : `${token('border.width.selected', '2px')} solid ${token(
                'color.background.accent.blue.subtle',
                '#579DFF'
              )}`,
          borderRadius: isCompactDefaultValueInput
            ? token('radius.medium')
            : 'radius.small',
          ...(isCompactDefaultValueInput && {
            background: token('color.background.input'),
            boxShadow: 'none',
            outline: 'none',
            paddingBlock: token('space.050', '4px'),
            height: `${TEXT_VALUE_MIN_TEXT_AREA_HEIGHT}px`,
            minHeight: `${TEXT_VALUE_MIN_TEXT_AREA_HEIGHT}px`
          }),
          textAlign: 'right',
          overflow: 'hidden',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
          position:
            isCompactDefaultValueInput || isRenderedInSidePanel || isBulkEdit
              ? 'relative'
              : 'absolute',
          boxSizing: 'border-box',
          // Override @atlaskit/textarea's font.body.large to match the cell's 14px font size.
          font: token('font.body', '14px')
        }}
        className="right-align-textarea"
        ref={textAreaRef}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isInvalid={isInvalid}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        minimumRows={isCompactDefaultValueInput ? 1 : undefined}
        rows={1}
        resize="none"
        wrap="off"
        data-testid="number-value-edit"
        aria-label={ariaLabel}
      />
    </Box>
  );
};

export default NumberValuePopoverEdit;
